/**
 * @fileoverview ClubOS Youth Academy — Batch Management
 * Training group setup: age group, coach assignment, capacity.
 * Changes: removed time/ground/date from cards, age-group summary card, PDF download, validations.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import SoftProgress from "components/SoftProgress";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import PropTypes from "prop-types";
import { academyService } from "api";
import FootballLoader from "components/FootballLoader";

const STATUS_COLOR = { Active: "success", Inactive: "secondary" };
const DEFAULT_AGE_GROUPS = ["U10", "U13", "U15", "U18"];
const AGE_GROUP_COLORS = { U10: "#f3e5f5", U13: "#e8f5e9", U15: "#fff3e0", U18: "#e0f7fa" };
const AGE_GROUP_TEXT  = { U10: "#7b1fa2", U13: "#2e7d32", U15: "#e65100", U18: "#006064" };

const emptyForm = {
  name: "", ageGroup: "U12", trainingDays: "", timeSlot: "", coachIds: [],
  location: "", maxCapacity: 20,
};

// ── Validate batch form ──────────────────────────────────────────────────────
function validateBatchForm(form) {
  const errs = {};
  if (!form.name.trim()) errs.name = "Batch name is required.";
  if (!form.ageGroup) errs.ageGroup = "Age group is required.";
  if (!form.maxCapacity || Number(form.maxCapacity) < 1) errs.maxCapacity = "Capacity must be at least 1.";
  if (form.coachIds.length === 0) errs.coachIds = "Assign at least one coach.";
  return errs;
}

// ── Generate PDF report ──────────────────────────────────────────────────────
async function downloadBatchesPDF(batches, ageGroups) {
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const now = new Date().toLocaleString("en-IN");

  doc.setFontSize(16);
  doc.text("Academy Batches Report", 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${now}`, 14, 25);

  // Summary by age group
  const grouped = (ageGroups || DEFAULT_AGE_GROUPS).reduce((acc, ag) => {
    const grp = batches.filter((b) => b.ageGroup === ag);
    if (grp.length > 0) acc[ag] = grp;
    return acc;
  }, {});

  let startY = 32;
  doc.setFontSize(12);
  doc.setTextColor(0);

  Object.entries(grouped).forEach(([ageGroup, grpBatches]) => {
    doc.text(`Age Group: ${ageGroup}`, 14, startY);
    startY += 4;

    autoTable(doc, {
      startY,
      head: [["Batch Code", "Name", "Coaches", "Enrolled", "Capacity", "Utilization", "Status"]],
      body: grpBatches.map((b) => [
        b.batchCode,
        b.name,
        b.coachNames?.join(", ") ?? b.coachName ?? "—",
        b.enrolledCount,
        b.maxCapacity,
        `${Math.round(b.utilizationPct ?? 0)}%`,
        b.status,
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [26, 115, 232], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 248, 255] },
      margin: { left: 14 },
    });

    startY = doc.lastAutoTable.finalY + 8;
    if (startY > 180) { doc.addPage(); startY = 20; }
  });

  doc.save(`batches-report-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// coachMap: { [fullName]: "Mr. FullName" }
function prefixCoachNames(names, coachMap) {
  if (!names?.length) return null;
  return names.map((n) => coachMap[n] ?? n).join(", ");
}

// ── Batch card — time, ground, date removed ──────────────────────────────────
function BatchCard({ batch, onEdit, coachMap }) {
  const pct = Math.min(Math.round(batch.utilizationPct ?? 0), 100);
  const color = pct >= 90 ? "error" : pct >= 70 ? "warning" : "success";

  return (
    <Card>
      <SoftBox p={2.5}>
        {/* Header */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <SoftBox>
            <SoftTypography variant="h6" fontWeight="bold" color="dark" lineHeight={1.3}>
              {batch.name}
            </SoftTypography>
            <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
              {batch.batchCode}
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={0.5} alignItems="center">
            <SoftBadge badgeContent={batch.ageGroup} color="info" size="xs" container />
            <SoftBadge badgeContent={batch.status} color={STATUS_COLOR[batch.status] ?? "secondary"} size="xs" container />
          </SoftBox>
        </SoftBox>

        {/* Capacity bar */}
        <SoftBox mb={1.5}>
          <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
            <SoftTypography variant="caption" color="text">
              {batch.enrolledCount}/{batch.maxCapacity} students
            </SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color={color}>
              {pct}% full
            </SoftTypography>
          </SoftBox>
          <SoftProgress value={pct} color={color} sx={{ height: 6 }} />
        </SoftBox>

        {/* Coaches */}
        {(batch.coachNames?.length > 0 || batch.coachName) && (
          <SoftBox display="flex" alignItems="flex-start" gap={0.5} mb={1.5}>
            <Icon sx={{ fontSize: "0.85rem", color: "info.main", mt: "2px" }}>person</Icon>
            <SoftTypography variant="caption" color="info">
              {prefixCoachNames(batch.coachNames, coachMap) ?? (coachMap[batch.coachName] ?? batch.coachName)}
            </SoftTypography>
          </SoftBox>
        )}

        <SoftButton variant="text" color="info" size="small" fullWidth onClick={() => onEdit(batch)}>
          <Icon sx={{ mr: 0.5 }}>edit</Icon> Edit Batch
        </SoftButton>
      </SoftBox>
    </Card>
  );
}

BatchCard.propTypes = {
  batch: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  coachMap: PropTypes.object.isRequired,
};

// ── Age Group Chart ───────────────────────────────────────────────────────────
// Horizontal grouped bar chart drawn on canvas — no extra library needed.
const CHART_PALETTE = {
  U10: { enrolled: "#ab47bc", capacity: "#e1bee7" },
  U13: { enrolled: "#66bb6a", capacity: "#c8e6c9" },
  U15: { enrolled: "#ffa726", capacity: "#ffe0b2" },
  U18: { enrolled: "#26c6da", capacity: "#b2ebf2" },
};

function AgeGroupChart({ batches, ageGroups }) {
  const canvasRef = useRef(null);

  const groups = (ageGroups || DEFAULT_AGE_GROUPS).map((ag) => {
    const grp = batches.filter((b) => b.ageGroup === ag);
    return {
      label: ag,
      enrolled: grp.reduce((s, b) => s + b.enrolledCount, 0),
      capacity: grp.reduce((s, b) => s + b.maxCapacity, 0),
      batches: grp.length,
    };
  }).filter((g) => g.batches > 0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || groups.length === 0) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth;
    const H = Math.max(groups.length * 60 + 60, 200);
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    canvas.style.height = H + "px";
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const PAD_LEFT  = 64;
    const PAD_RIGHT = 60;
    const PAD_TOP   = 24;
    const ROW_H     = 56;
    const BAR_H     = 16;
    const TRACK_H   = 6;
    const maxCap    = Math.max(...groups.map((g) => g.capacity), 1);
    const chartW    = W - PAD_LEFT - PAD_RIGHT;

    // Title
    ctx.font = "bold 13px Inter, sans-serif";
    ctx.fillStyle = "#344767";
    ctx.fillText("Enrollment by Age Group", PAD_LEFT, 16);

    groups.forEach((g, i) => {
      const y = PAD_TOP + i * ROW_H + 20;
      const palette = CHART_PALETTE[g.label] ?? { enrolled: "#90caf9", capacity: "#e3f2fd" };
      const capW    = Math.round((g.capacity / maxCap) * chartW);
      const enrW    = g.capacity > 0 ? Math.round((g.enrolled / maxCap) * chartW) : 0;
      const pct     = g.capacity > 0 ? Math.round((g.enrolled / g.capacity) * 100) : 0;

      // Label
      ctx.font = "bold 11px Inter, sans-serif";
      ctx.fillStyle = "#344767";
      ctx.textAlign = "right";
      ctx.fillText(g.label, PAD_LEFT - 8, y + BAR_H / 2 + 4);

      // Capacity track (thin background)
      const trackY = y + (BAR_H - TRACK_H) / 2;
      ctx.fillStyle = palette.capacity;
      ctx.beginPath();
      ctx.roundRect(PAD_LEFT, trackY, capW, TRACK_H, 3);
      ctx.fill();

      // Enrolled bar (tall, rounded)
      if (enrW > 0) {
        const grad = ctx.createLinearGradient(PAD_LEFT, 0, PAD_LEFT + enrW, 0);
        grad.addColorStop(0, palette.enrolled);
        grad.addColorStop(1, palette.enrolled + "cc");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(PAD_LEFT, y, enrW, BAR_H, 4);
        ctx.fill();
      }

      // Inline labels: enrolled/capacity  %
      ctx.textAlign = "left";
      ctx.font = "11px Inter, sans-serif";
      ctx.fillStyle = "#7b809a";
      ctx.fillText(`${g.enrolled}/${g.capacity}`, PAD_LEFT + capW + 8, y + BAR_H / 2 + 4);

      // % badge on bar end
      if (enrW > 28) {
        ctx.font = "bold 9px Inter, sans-serif";
        ctx.fillStyle = "#fff";
        ctx.textAlign = "right";
        ctx.fillText(`${pct}%`, PAD_LEFT + enrW - 5, y + BAR_H / 2 + 3);
      }

      // Sub-label: batch count
      ctx.textAlign = "right";
      ctx.font = "10px Inter, sans-serif";
      ctx.fillStyle = "#ababab";
      ctx.fillText(`${g.batches} batch${g.batches !== 1 ? "es" : ""}`, PAD_LEFT - 8, y + BAR_H + 13);
    });
  }, [groups]);

  if (groups.length === 0) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <SoftBox p={2.5}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <SoftBox>
            <SoftTypography variant="h6" fontWeight="bold" color="dark">Age Group Overview</SoftTypography>
            <SoftTypography variant="caption" color="text">Enrolled players vs total capacity per age group</SoftTypography>
          </SoftBox>
          {/* Legend */}
          <SoftBox display="flex" gap={2} alignItems="center">
            <SoftBox display="flex" alignItems="center" gap={0.5}>
              <SoftBox sx={{ width: 12, height: 12, borderRadius: "3px", backgroundColor: "#42a5f5" }} />
              <SoftTypography variant="caption" color="text">Enrolled</SoftTypography>
            </SoftBox>
            <SoftBox display="flex" alignItems="center" gap={0.5}>
              <SoftBox sx={{ width: 24, height: 4, borderRadius: "2px", backgroundColor: "#bbdefb" }} />
              <SoftTypography variant="caption" color="text">Capacity</SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>
        <canvas ref={canvasRef} style={{ width: "100%", display: "block" }} />
      </SoftBox>
    </Card>
  );
}

AgeGroupChart.propTypes = {
  batches: PropTypes.array.isRequired,
  ageGroups: PropTypes.array,
};

function AcademyBatches() {
  const [batches, setBatches]         = useState([]);
  const [coaches, setCoaches]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [ageGroups, setAgeGroups]       = useState(DEFAULT_AGE_GROUPS);
  const [trainingDays, setTrainingDays] = useState(["Sat-Sun", "Fri-Sat-Sun"]);
  const [timeSlots, setTimeSlots]       = useState(["06:00-08:00", "07:00-09:00", "16:00-18:00", "17:00-19:00", "18:00-20:00"]);
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [coachSelectOpen, setCoachSelectOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      academyService.getBatches(),
      academyService.getCoaches({ status: "Active" }),
      academyService.getAgeGroups(),
      academyService.getTrainingDaySchedules(),
      academyService.getTimeSlots(),
    ])
      .then(([b, c, ag, td, ts]) => {
        setBatches(Array.isArray(b) ? b : []);
        setCoaches(Array.isArray(c) ? c : []);
        setAgeGroups(Array.isArray(ag) && ag.length ? ag : DEFAULT_AGE_GROUPS);
        setTrainingDays(Array.isArray(td) && td.length ? td : ["Sat-Sun", "Fri-Sat-Sun"]);
        setTimeSlots(Array.isArray(ts) && ts.length ? ts : ["06:00-08:00", "07:00-09:00", "16:00-18:00", "17:00-19:00", "18:00-20:00"]);
      })
      .catch((err) => {
        console.error("Failed to load batches:", err);
        setBatches([]);
        setCoaches([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFieldErrors({});
    setSaveError("");
    setDialogOpen(true);
  };

  const openEdit = (batch) => {
    setEditing(batch);
    setForm({
      name: batch.name, ageGroup: batch.ageGroup, trainingDays: batch.trainingDays ?? "",
      timeSlot: batch.timeSlot ?? "",
      coachIds: batch.coachIds ?? (batch.coachId ? [batch.coachId] : []),
      location: batch.location ?? "", maxCapacity: batch.maxCapacity,
    });
    setFieldErrors({});
    setSaveError("");
    setDialogOpen(true);
  };

  const f = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSave = async () => {
    const errs = validateBatchForm(form);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setSaveError("Please fix the highlighted errors before saving.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        ...form,
        maxCapacity: Number(form.maxCapacity),
        coachIds: form.coachIds,
        coachId: form.coachIds[0] ?? null,
      };
      if (editing) await academyService.updateBatch(editing.id, payload);
      else await academyService.createBatch(payload);
      setDialogOpen(false);
      load();
    } catch (e) {
      console.error(e);
      setSaveError(e?.detail ?? e?.title ?? "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // Build fullName → "Salutation FullName" map for display
  const coachMap = Object.fromEntries(
    coaches.map((c) => [c.fullName, c.salutation ? `${c.salutation} ${c.fullName}` : c.fullName])
  );

  const totalCapacity = batches.reduce((s, b) => s + b.maxCapacity, 0);
  const totalEnrolled = batches.reduce((s, b) => s + b.enrolledCount, 0);
  const overallPct    = totalCapacity > 0 ? Math.round(totalEnrolled / totalCapacity * 100) : 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Training Batches</SoftTypography>
            <SoftTypography variant="body2" color="text">
              {batches.length} batches · {totalEnrolled}/{totalCapacity} students ({overallPct}% utilization)
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1}>
            <SoftButton variant="outlined" color="info" startIcon={<Icon>download</Icon>}
              onClick={() => downloadBatchesPDF(batches, ageGroups)}>
              Download Report
            </SoftButton>
            <SoftButton variant="gradient" color="info" startIcon={<Icon>add</Icon>} onClick={openAdd}>
              Add Batch
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {/* Age Group Chart */}
        <AgeGroupChart batches={batches} ageGroups={ageGroups} />

        {loading ? (
          <FootballLoader label="Loading batches…" />
        ) : (
          <Grid container spacing={3}>
            {batches.map((b) => (
              <Grid key={b.id} item xs={12} sm={6} lg={4}>
                <BatchCard batch={b} onEdit={openEdit} coachMap={coachMap} />
              </Grid>
            ))}
            {batches.length === 0 && (
              <Grid item xs={12}>
                <SoftBox p={4} textAlign="center">
                  <SoftTypography variant="caption" color="text">No batches yet</SoftTypography>
                </SoftBox>
              </Grid>
            )}
          </Grid>
        )}
      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Batch" : "Create Training Batch"}</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{saveError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Batch Name" value={form.name}
                onChange={f("name")} size="small"
                error={Boolean(fieldErrors.name)} helperText={fieldErrors.name} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth required label="Age Group" value={form.ageGroup}
                onChange={f("ageGroup")} size="small"
                error={Boolean(fieldErrors.ageGroup)} helperText={fieldErrors.ageGroup}>
                {ageGroups.map((ag) => <MenuItem key={ag} value={ag}>{ag}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Max Capacity" type="number" value={form.maxCapacity}
                onChange={f("maxCapacity")} size="small"
                error={Boolean(fieldErrors.maxCapacity)} helperText={fieldErrors.maxCapacity} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Time Slot" value={form.timeSlot}
                onChange={f("timeSlot")} size="small">
                <MenuItem value=""><em>Select time slot…</em></MenuItem>
                {timeSlots.map((ts) => <MenuItem key={ts} value={ts}>{ts}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Training Days" value={form.trainingDays}
                onChange={f("trainingDays")} size="small">
                {trainingDays.map((td) => <MenuItem key={td} value={td}>{td}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Location / Ground" value={form.location}
                onChange={f("location")} size="small" />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={Boolean(fieldErrors.coachIds)}>
                <InputLabel>Assigned Coaches *</InputLabel>
                <Select
                  multiple
                  open={coachSelectOpen}
                  onOpen={() => setCoachSelectOpen(true)}
                  onClose={() => setCoachSelectOpen(false)}
                  value={form.coachIds}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, coachIds: e.target.value }));
                    setFieldErrors((prev) => ({ ...prev, coachIds: undefined }));
                    setCoachSelectOpen(false);
                  }}
                  input={<OutlinedInput label="Assigned Coaches *" />}
                  renderValue={(selected) => (
                    <SoftBox display="flex" flexWrap="wrap" gap={0.5}>
                      {selected.map((id) => {
                        const c = coaches.find((x) => x.id === id);
                        return <Chip key={id} label={c ? c.fullName : id} size="small" />;
                      })}
                    </SoftBox>
                  )}
                >
                  {coaches.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.fullName} — {c.role}</MenuItem>
                  ))}
                </Select>
                {fieldErrors.coachIds && (
                  <SoftTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {fieldErrors.coachIds}
                  </SoftTypography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editing ? "Update" : "Create Batch"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default AcademyBatches;
